'use client'

import * as React from 'react'
import { Link, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { doc, getDoc } from 'firebase/firestore'
import { Button } from 'components/ui/button'
import { Badge } from 'components/ui/badge'
import { cn } from 'lib/utils'
import { toast } from 'sonner'
import { db } from '../../../../firebase'
import { ProjectProvider } from './project-context'
import { useUser } from 'components/context/user-context'
import { getUserId, isUserAssociatedWithProject } from 'lib/access'

const projectTabs = [
  { label: 'Overview', segment: 'overview' },
  { label: 'Team', segment: 'team' },
  { label: 'Workplan', segment: 'workplan' },
  { label: 'Budget', segment: 'budget' },
  { label: 'Tracking', segment: 'tracking' },
  { label: 'M&E', segment: 'monitoring' },
  { label: 'Partners', segment: 'partners' },
  { label: 'Documents', segment: 'documents' },
  { label: 'Risks', segment: 'risks' },
  { label: 'Audit', segment: 'audit' },
  { label: 'Issues', segment: 'issues' },
]

const STATUS_LABELS = {
  planning: 'Planning',
  active: 'Active',
  'on-hold': 'On Hold',
  completed: 'Completed',
  closed: 'Closed',
  'at-risk': 'At Risk',
}

const STATUS_BADGE_STYLES = {
  planning: 'bg-muted text-muted-foreground border-border',
  active: 'bg-success/15 text-success border-success/30',
  'on-hold': 'bg-muted text-muted-foreground border-border',
  completed: 'bg-primary/15 text-primary border-primary/30',
  closed: 'bg-primary/10 text-primary border-primary/30',
  'at-risk': 'bg-warning/15 text-warning border-warning/30',
}

const statusAliases = {
  on_hold: 'on-hold',
  onhold: 'on-hold',
  atrisk: 'at-risk',
}

function normalizeStatus(status) {
  if (!status) return 'planning'
  const normalized = String(status)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/_/g, '-')
  return statusAliases[normalized] || normalized
}

function StatusBadge({ status }) {
  const normalized = normalizeStatus(status)
  const style = STATUS_BADGE_STYLES[normalized] || STATUS_BADGE_STYLES.planning
  return (
    <Badge className={`text-[10px] border ${style}`}>
      {STATUS_LABELS[normalized] || normalized.replace(/-/g, ' ')}
    </Badge>
  )
}

export default function ProjectLayout() {
  const { id } = useParams()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { user, accessLevel } = useUser()
  const [project, setProject] = React.useState(null)
  const [isLoading, setIsLoading] = React.useState(true)

  React.useEffect(() => {
    let isMounted = true

    async function loadProject() {
      if (!id) return
      setIsLoading(true)
      try {
        const snapshot = await getDoc(doc(db, 'projects', id))
        if (!snapshot.exists()) {
          toast.error('Project not found')
          navigate('/dashboard/projects')
          return
        }
        if (!isMounted) return
        const data = snapshot.data() || {}
        const normalized = {
          id: snapshot.id,
          ...data,
          sdgMappings: Array.isArray(data.sdgMappings) ? data.sdgMappings : [],
          strategicPillars: Array.isArray(data.strategicPillars) ? data.strategicPillars : [],
          budgetItems: Array.isArray(data.budgetItems) ? data.budgetItems : [],
          projectMembers: Array.isArray(data.projectMembers) ? data.projectMembers : [],
        }
        const limited = accessLevel === 'limited'
        const currentUserId = getUserId(user)
        if (limited && !isUserAssociatedWithProject(normalized, currentUserId)) {
          toast.error('You do not have access to this project.')
          navigate('/dashboard/projects')
          return
        }
        setProject(normalized)
      } catch (error) {
        toast.error('Failed to load project', {
          description: error?.message || 'Please try again.',
        })
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    loadProject()

    return () => {
      isMounted = false
    }
  }, [id, navigate, accessLevel, user?.userId, user?.uid])

  if (isLoading) {
    return (
      <div className="space-y-4 p-6">
        <div className="py-16 text-center text-muted-foreground">Loading project...</div>
      </div>
    )
  }

  if (!project) {
    return (
      <div className="space-y-4 p-6">
        <div className="py-16 text-center text-muted-foreground">Project not found</div>
      </div>
    )
  }

  return (
    <ProjectProvider value={{ project, setProject }}>
      <div className="space-y-4 p-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" asChild className="size-8">
            <Link to="/dashboard/projects">
              <ArrowLeft className="size-4" />
            </Link>
          </Button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold text-foreground truncate">
                {project.title || project.name || 'Untitled project'}
              </h1>
              <StatusBadge status={project.status} />
            </div>
            <p className="text-xs text-muted-foreground">
              {(project.department || project.departmentId || 'Department not set')} |{' '}
              {project.principalInvestigatorName || 'PI'}
            </p>
          </div>
        </div>

        <div className="flex gap-1 overflow-x-auto border-b pb-px -mb-px">
          {projectTabs.map((tab) => {
            const href = `/dashboard/projects/${project.id}/${tab.segment}`
            const isActive = pathname === href || (tab.segment === 'overview' && pathname === `/dashboard/projects/${project.id}`)
            return (
              <Link
                key={tab.segment}
                to={href}
                className={cn(
                  'px-3 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors',
                  isActive
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30',
                )}
              >
                {tab.label}
              </Link>
            )
          })}
        </div>

        <Outlet />
      </div>
    </ProjectProvider>
  )
}
